import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { formatWithWebp } from './helpers';
import FallbackIcon from './assets/fallback_logo_kitten.svg';
import { WEBP, SOURCE_TAG_NAME } from './constants';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

/**
 * An image container
 * with webp && lazy load support
 * Just use it instead of <img> tag.
 */
const WithSeoWrapper = ({ attribs, children }) =>
  attribs ? <div {...attribs}>{children}</div> : children;

WithSeoWrapper.propTypes = {
  attribs: PropTypes.object,
  children: PropTypes.node,
};

export default class Image extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showFallback: true,
    };
    this.image = React.createRef();
  }

  componentDidMount() {
    const { useLazyLoad, src } = this.props;
    if (useLazyLoad && src) this.trackLoad();
  }

  componentDidUpdate(prevProps) {
    const { src, useLazyLoad } = this.props;
    if (src && prevProps.src !== src && useLazyLoad) this.trackLoad();
  }

  componentWillUnmount() {
    const { useLazyLoad } = this.props;
    if (useLazyLoad) this.removeEventListeners();
  }

  trackLoad = () => {
    this.handleLazyLoad();
    this.addEventListeners();
  };

  addEventListeners = () => {
    document.addEventListener('scroll', this.handleLazyLoad);
    window.addEventListener('resize', this.handleLazyLoad);
    // window.addEventListener('orientationchange', this.handleLazyLoad);
  };

  removeEventListeners = () => {
    document.removeEventListener('scroll', this.handleLazyLoad);
    window.removeEventListener('resize', this.handleLazyLoad);
    // window.removeEventListener('orientationchange', this.handleLazyLoad);
  };

  getImageNode = () => (this.image || {}).current || null;

  setImageSrc = (imageNode) => {
    imageNode.src = imageNode.dataset.src;
    // imageNode.srcset = imageNode.dataset.src;
  };

  setPictureSrc = (pictureNode) => {
    pictureNode.firstChild.srcset = pictureNode.firstChild.dataset.srcset;
  };

  handleLazyLoad = () => {
    const { useWebp } = this.props;
    const image = this.getImageNode();
    const picture = image.parentNode;
    const picturePosition = picture.getBoundingClientRect();

    if (
      picturePosition.top <= window.innerHeight + window.pageYOffset &&
      picturePosition.bottom >= 0
    ) {
      // ===
      if (useWebp) {
        this.setPictureSrc(picture);
      } else {
        this.setImageSrc(image);
      }
      // ===
      this.removeEventListeners();
    }
  };

  handleError = (useWebp) => {
    const image = this.getImageNode();
    const picture = image.parentNode;

    if (useWebp && picture.firstChild && picture.firstChild.tagName === SOURCE_TAG_NAME) {
      picture.removeChild(picture.firstChild);
      this.setImageSrc(image);
    } else {
      this.setState({ showFallback: true });
    }
  };

  render() {
    const {
      src,
      alt,
      className,
      fallbackClassName,
      pictureClassName,
      useWebp,
      useLazyLoad,
      hasSchemaAttributes,
      ...inheritedProps
    } = this.props;

    const { showFallback } = this.state;
    const schemaAttributes =
      hasSchemaAttributes ?
        {
          itemProp: 'contentUrl',
        }
      : {};
    const schemaWrapperAttributes =
      hasSchemaAttributes ?
        {
          itemScope: 'itemScope',
          itemType: 'http://schema.org/ImageObject',
        }
      : null;
    return (
      <picture className={cx('picture', pictureClassName)}>
        {useWebp && (
          <source
            data-srcset={formatWithWebp(src)}
            srcSet={useLazyLoad ? null : formatWithWebp(src)}
            type={WEBP}
          />
        )}
        <WithSeoWrapper attribs={schemaWrapperAttributes}>
          <img
            className={cx(className, 'imageElement', showFallback && 'hide')}
            onError={() => {
              this.handleError(useWebp);
            }}
            onLoad={() => {
              this.setState({ showFallback: false });
            }}
            loading="lazy"
            ref={this.image}
            src={useLazyLoad ? null : src}
            data-src={src}
            alt={alt}
            {...inheritedProps}
            {...schemaAttributes}
          />
        </WithSeoWrapper>
        {showFallback && (
          <div className={cx(className, fallbackClassName, 'fallbackBackground')}>
            <FallbackIcon className={cx('fallbackIcon')} />
          </div>
        )}
      </picture>
    );
  }
}

Image.defaultProps = {
  useWebp: false,
  useLazyLoad: true,
  alt: '',
};

Image.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  useWebp: PropTypes.bool,
  useLazyLoad: PropTypes.bool,
  className: PropTypes.string,
  fallbackClassName: PropTypes.string,
  pictureClassName: PropTypes.string,
  hasSchemaAttributes: PropTypes.bool,
};
